import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';
import { getEnglishLanguageId } from 'utils/i18n';

/**
 * Channel data processing utilities
 * Extracts locale handling and data transformation logic
 */
export const createChannelDataService = () => {
  /**
   * Get specific language locale for a channel
   */
  const getChannelLocale = (channel, languageId) => {
    return channel.channel_locale.find((locale) => locale.language_id === languageId);
  };

  /**
   * Process raw channel data with locale fallback logic
   * @param {Array} orgChannels - Raw channels from storage
   * @returns {Array} Processed channels with locale_channel and locale_english
   */
  const processChannelsWithLocales = (orgChannels) => {
    const currentLanguageId = Storages.MainStorage.getItem(StorageKeys.Keys.LANGUAGEID);
    const englishLanguageId = getEnglishLanguageId();

    const processedChannels = orgChannels
      .map((channel) => {
        // Get locale for current language and English
        const currentLocale = getChannelLocale(channel, currentLanguageId);
        const englishLocale = getChannelLocale(channel, englishLanguageId);
        const api_key = Storages.MainStorage.getItem(StorageKeys.Keys.LICENSEAPIKEY)?.api_key

        // If neither locale exists, return null to filter out later
        if (!currentLocale && !englishLocale) return null;
        const channel_url = channel?.channel_url ?? "";
        const updated_url = channel_url.includes("?")
          ? `${channel_url}&apikey=${api_key}`
          : `${channel_url}?apikey=${api_key}`;
        return {
          id: channel.id,
          vod: channel.vod??false,
          live: channel.live??false,
          radio: channel.radio??false,
          channel_type: channel.channel_type,
          channel_id: {
            channel: {
              id: channel.id,
              channel_url: updated_url,
              image_url: channel.channel_image,
            },
            locale_channel: currentLocale || englishLocale, // Fallback to English if current language not available
            locale_english: englishLocale, // Always include English locale if available
          },
          organization_id: channel.organization,
        };
      })
      .filter(Boolean); // Remove null entries

    return processedChannels;
  };

  /**
   * Get all org channels from storage
   */
  const getOrgChannels = () => {
    return Storages.MainStorage.getItem(StorageKeys.Keys.ORGCHANNEL) || [];
  };

  /**
   * Validate if channels have valid locales
   */
  const validateChannelLocales = (channelsList) => {
    return channelsList.every((item) => {
      const channel_id = item['channel_id'];
      return channel_id && channel_id['locale_channel'] && channel_id['locale_channel'].locale_name;
    });
  };

  /**
   * Filter processed channels by media mode (Live TV vs Radio).
   * Expects output from processChannelsWithLocales().
   */
  const filterChannelsByMode = (processedChannels, isRadio) => {
    return (processedChannels || []).filter((channel) => Boolean(channel.radio) === Boolean(isRadio));
  };

  /**
   * Returns true when at least one playable channel exists for the given mode.
   */
  const hasChannelsForMode = (processedChannels, isRadio) => {
    return filterChannelsByMode(processedChannels, isRadio).length > 0;
  };

  const hasRadioChannels = (processedChannels) => {
    return hasChannelsForMode(processedChannels, true);
  };

  return {
    getChannelLocale,
    processChannelsWithLocales,
    getOrgChannels,
    validateChannelLocales,
    filterChannelsByMode,
    hasChannelsForMode,
    hasRadioChannels,
  };
};

